import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FaFile, FaFileArchive, FaFileExcel, FaFileImage, FaFilePdf, FaFileWord, FaFileVideo } from 'react-icons/fa';
import { BsWrenchAdjustable } from 'react-icons/bs';
import { FiBarChart2, FiLayers, FiShield, FiUsers } from 'react-icons/fi';
import { MdLocalShipping, MdOutlineClose } from 'react-icons/md';
import { RiMoneyDollarCircleLine, RiSteering2Line } from 'react-icons/ri';
import { useDispatch, useSelector } from 'react-redux';
import { clearUploadedFiles, uploadFiles } from '../store/Slices/FileSlice';
import { uploadedFuelFiles } from '../store/Slices/FuelSlice';
import '../styles/FileUpload.css';
import {
    uploadIcon,
    tripSidebarIcon,
    fuelSidebarIcon,
    inventoryIcon,
    contractSidebarIcon,
    accessManagementIcon
} from '../utils/svgs';
import { errorNotify, successNotify } from '../utils/toast';
import { getOriginalFileName, getShortFileName } from '../utils/helper';
import { apiService } from '../services/apiService';
import DeleteModal from './modals/DeleteModal';

const getFilesBaseUrl = () => {
    const rawFilesUrl = String(process.env.REACT_APP_API_FILES_URL || '').trim();
    if (rawFilesUrl) return rawFilesUrl;

    const rawBaseUrl = String(process.env.REACT_APP_API_BASE_URL || '').trim();
    if (rawBaseUrl) {
        return rawBaseUrl.replace(/\/api\/?$/, '');
    }
    return '';
};

const FILES_BASE_URL = getFilesBaseUrl();

const normalizeToArray = (value) => {
    if (Array.isArray(value)) return value;
    if (value === null || value === undefined || value === '') return [];
    return [value];
};

const isDirectUrl = (value) => /^(https?:)?\/\//i.test(String(value || '').trim());
const isObjectUrl = (value) => /^blob:/i.test(String(value || '').trim());
const isSpecialUrl = (value) => /^(blob:|data:)/i.test(String(value || '').trim());

const joinFileUrl = (baseUrl, path) => {
    const normalizedBase = String(baseUrl || '').trim().replace(/\/+$/, '');
    const normalizedPath = String(path || '').trim().replace(/^\/+/, '');

    if (!normalizedBase) return normalizedPath;
    if (!normalizedPath) return normalizedBase;

    return `${normalizedBase}/${normalizedPath}`;
};

const getRelativeFilePath = (value) => {
    const rawValue = String(value || '').trim();
    if (!rawValue) return '';

    if (isSpecialUrl(rawValue)) {
        return rawValue;
    }

    if (FILES_BASE_URL) {
        const normalizedBase = FILES_BASE_URL.replace(/\/+$/, '');
        if (rawValue.startsWith(normalizedBase)) {
            return rawValue.slice(normalizedBase.length).replace(/^\/+/, '');
        }
    }

    if (isDirectUrl(rawValue)) {
        try {
            return new URL(rawValue).pathname.replace(/^\/+/, '');
        } catch {
            return rawValue;
        }
    }

    return rawValue.replace(/^\/+/, '');
};

const buildPreviewUrl = (value) => {
    const rawValue = String(value || '').trim();
    if (!rawValue) return '';
    if (isSpecialUrl(rawValue) || isDirectUrl(rawValue)) return rawValue;
    return joinFileUrl(getFilesBaseUrl(), rawValue);
};

const resolveFilePath = (fileEntry) => {
    if (typeof fileEntry === 'string') return fileEntry;
    if (!fileEntry || typeof fileEntry !== 'object') return '';
    return (
        fileEntry?.file_path ||
        fileEntry?.fileName ||
        fileEntry?.file_name ||
        fileEntry?.file_url ||
        fileEntry?.full_url ||
        fileEntry?.media_url ||
        fileEntry?.path ||
        fileEntry?.url ||
        fileEntry?.src ||
        fileEntry?.document ||
        fileEntry?.file ||
        fileEntry?.image ||
        (typeof fileEntry?.data === 'string' ? fileEntry.data : '') ||
        fileEntry?.data?.file_path ||
        fileEntry?.data?.fileName ||
        fileEntry?.data?.file_name ||
        fileEntry?.data?.file_url ||
        fileEntry?.data?.full_url ||
        fileEntry?.data?.media_url ||
        fileEntry?.data?.path ||
        fileEntry?.data?.url ||
        fileEntry?.data?.src ||
        fileEntry?.data?.document ||
        fileEntry?.data?.file ||
        fileEntry?.data?.image ||
        fileEntry?.original_name ||
        fileEntry?.originalName ||
        fileEntry?.name ||
        ''
    );
};

const parseNumericFileId = (value) => {
    if (value === undefined || value === null) return null;

    const trimmedValue = String(value).trim();
    if (!/^\d+$/.test(trimmedValue)) return null;

    const numericValue = Number(trimmedValue);
    if (!Number.isInteger(numericValue) || numericValue <= 0) return null;

    return numericValue;
};

const resolveGalleryFileId = (fileEntry) => {
    if (!fileEntry || typeof fileEntry !== 'object') return null;

    const candidateIds = [
        fileEntry?.gallery_id,
        fileEntry?.galleryId,
        fileEntry?.file_id,
        fileEntry?.fileId,
        fileEntry?.media_id,
        fileEntry?.mediaId,
        fileEntry?.delete_id,
        fileEntry?.deleteId,
        fileEntry?.id,
        fileEntry?._id,
        fileEntry?.data?.gallery_id,
        fileEntry?.data?.galleryId,
        fileEntry?.data?.file_id,
        fileEntry?.data?.fileId,
        fileEntry?.data?.media_id,
        fileEntry?.data?.mediaId,
        fileEntry?.data?.delete_id,
        fileEntry?.data?.deleteId,
        fileEntry?.data?.id,
        fileEntry?.data?._id,
    ];

    for (const candidate of candidateIds) {
        const parsedId = parseNumericFileId(candidate);
        if (parsedId !== null) {
            return parsedId;
        }
    }

    return null;
};

const hasDirectFilePath = (fileEntry) => {
    if (typeof fileEntry === 'string') return String(fileEntry).trim() !== '';
    return Boolean(resolveFilePath(fileEntry));
};

const extractUploadedFileEntries = (value) => {
    const collectedFiles = [];
    const visited = new Set();
    const queue = normalizeToArray(value);

    while (queue.length) {
        const current = queue.shift();

        if (current === null || current === undefined || current === '') {
            continue;
        }

        if (typeof current === 'string') {
            if (current.trim()) {
                collectedFiles.push(current.trim());
            }
            continue;
        }

        if (typeof current !== 'object') {
            continue;
        }

        if (visited.has(current)) {
            continue;
        }
        visited.add(current);

        if (hasDirectFilePath(current)) {
            collectedFiles.push(current);
            continue;
        }

        [
            current?.data,
            current?.files,
            current?.results,
            current?.items,
            current?.rows,
            current?.media,
            current?.attachments,
            current?.uploadedFiles,
            current?.uploaded_files,
        ].forEach((entry) => {
            normalizeToArray(entry).forEach((item) => queue.push(item));
        });

        Object.values(current).forEach((entry) => {
            if (entry && typeof entry === 'object') {
                queue.push(entry);
            }
        });
    }

    return collectedFiles;
};

const normalizeDisplayFile = (fileEntry) => {
    const resolvedPath = resolveFilePath(fileEntry);
    const normalizedPath = getRelativeFilePath(resolvedPath);
    const normalizedOriginalName =
        typeof fileEntry === 'object' && fileEntry !== null
            ? String(fileEntry?.originalName || fileEntry?.original_name || normalizedPath || resolvedPath || '').trim()
            : normalizedPath || resolvedPath;
    const normalizedPreviewUrl =
        typeof fileEntry === 'object' && fileEntry !== null && fileEntry?.previewUrl
            ? String(fileEntry.previewUrl).trim()
            : (resolvedPath ? buildPreviewUrl(resolvedPath) : null);

    return {
        ...(typeof fileEntry === 'object' && fileEntry !== null ? fileEntry : {}),
        fileName: normalizedPath || resolvedPath,
        originalName: normalizedOriginalName,
        previewUrl: normalizedPreviewUrl || null,
    };
};

const mergeFilesList = (existingFiles, incomingFiles, maxFiles = null) => {
    const safeExistingFiles = normalizeToArray(existingFiles);
    const safeIncomingFiles = normalizeToArray(incomingFiles);

    if (maxFiles === 1 && safeIncomingFiles.length > 0) {
        return safeIncomingFiles.slice(-1);
    }

    const normalizedExistingFiles = safeExistingFiles.map((file) => normalizeDisplayFile(file));
    const existingFileNames = normalizedExistingFiles.map((file) => file.fileName).filter(Boolean);
    const filteredIncomingFiles = safeIncomingFiles.filter(
        (file) => file?.fileName && !existingFileNames.includes(file.fileName)
    );
    const combinedFiles = [...safeExistingFiles, ...filteredIncomingFiles];

    if (maxFiles && combinedFiles.length > maxFiles) {
        return combinedFiles.slice(-maxFiles);
    }

    return combinedFiles;
};

const VideoThumbnail = ({ src, alt }) => {
    const [hasError, setHasError] = useState(false);
    const videoRef = useRef(null);

    const videoSrc = useMemo(() => {
        if (!src) return '';
        return src.includes('#') ? src : `${src}#t=0.001`;
    }, [src]);

    if (hasError || !src) {
        return (
            <div className='file_icon_preview'>
                <FaFileVideo style={{ color: '#e0245e', fontSize: '26px' }} />
            </div>
        );
    }

    return (
        <div className='file_video_thumb_wrapper'>
            <video
                ref={videoRef}
                src={videoSrc}
                className='file_video_preview'
                muted
                playsInline
                preload="metadata"
                onLoadedData={(e) => {
                    try {
                        if (e.target.currentTime === 0) {
                            e.target.currentTime = 0.001;
                        }
                    } catch (_) { }
                }}
                onError={() => setHasError(true)}
            />
            <span className='video_play_badge'>
                <svg width="8" height="8" viewBox="0 0 24 24" fill="currentColor">
                    <polygon points="6 4 20 12 6 20 6 4" />
                </svg>
            </span>
        </div>
    );
};

const FileUpload = ({
    destination = 'service-entry',
    files = [],
    setFiles,
    maxFileSize = 10,
    className = '',
    disabled = false,
    tripId = null,
    maxFiles = null,
    minFiles = null,
    consumeUploadedFiles = true,
    accept = "image/*,.pdf,.doc,.docx,.xls,.xlsx",
    tabIndex
}) => {
    const dispatch = useDispatch();

    const { uploadLoading, uploadedFiles } = useSelector((state) => state.files);

    const [dragActive, setDragActive] = useState(false);
    const inputRef = useRef(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [fileToDelete, setFileToDelete] = useState(null);
    const [deleteLoading, setDeleteLoading] = useState(false);

    const normalizedDeleteFile = fileToDelete?.file
        ? normalizeDisplayFile(fileToDelete.file)
        : null;
    const deleteFileLabel =
        normalizedDeleteFile?.originalName ||
        normalizedDeleteFile?.fileName ||
        "";
    const deleteFileName = deleteFileLabel
        ? getOriginalFileName(deleteFileLabel) || deleteFileLabel
        : "this file";
    const shortDeleteFileName = deleteFileLabel
        ? getShortFileName(deleteFileLabel) || deleteFileName
        : "this file";

    const applyNormalizedFiles = useCallback((filesArr) => {
        const newFiles = normalizeToArray(filesArr)
            .map((fileEntry) => {
                const normalizedFile = normalizeDisplayFile(fileEntry);
                if (!normalizedFile.fileName) return null;

                const galleryFileId = resolveGalleryFileId(fileEntry);
                const deleteKey = galleryFileId !== null ? String(galleryFileId) : "";
                const fileLabel = normalizedFile.originalName || normalizedFile.fileName;
                const previewUrl = (isImageFile(fileLabel) || isVideoFile(fileLabel))
                    ? (normalizedFile.previewUrl || buildPreviewUrl(normalizedFile.fileName))
                    : null;

                return {
                    ...normalizedFile,
                    id: galleryFileId ?? undefined,
                    deleteKey: deleteKey || undefined,
                    previewUrl,
                    backend: true
                };
            })
            .filter(Boolean);

        if (!newFiles.length) {
            if (inputRef.current) {
                inputRef.current.value = '';
            }
            return [];
        }

        if (typeof setFiles === 'function') {
            setFiles((prev) => mergeFilesList(prev, newFiles, maxFiles));
        }

        if (inputRef.current) {
            inputRef.current.value = '';
        }

        return newFiles;
    }, [maxFiles, setFiles]);

    const isFileAccepted = (file, acceptedTypesString) => {
        if (!file || !acceptedTypesString) return true;

        const acceptedTypes = acceptedTypesString
            .split(',')
            .map((type) => type.trim().toLowerCase())
            .filter(Boolean);

        if (!acceptedTypes.length) return true;

        const fileName = String(file?.name || file?.fileName || '').toLowerCase();
        const fileType = String(file?.type || '').toLowerCase();

        return acceptedTypes.some((type) => {
            if (type === '*/*') return true;
            if (type.endsWith('/*')) {
                const majorType = type.slice(0, -2);
                return fileType.startsWith(`${majorType}/`);
            }
            if (type.startsWith('.')) {
                return fileName.endsWith(type);
            }
            return fileType === type;
        });
    };

    const validateFileBeforeUpload = (file) => {
        if (!file) {
            errorNotify('No file selected for upload');
            return false;
        }

        if (!isFileAccepted(file, accept)) {
            errorNotify(`Invalid file type. Allowed: ${accept}`);
            return false;
        }

        return true;
    };

    const handleDrag = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const uploadSelectedFiles = async (selectedFiles = []) => {
        const filesToUpload = Array.from(selectedFiles || []);
        if (!filesToUpload.length) {
            errorNotify('No file selected for upload');
            return;
        }

        const remainingSlots = maxFiles ? Math.max(maxFiles - files.length, 0) : filesToUpload.length;
        const uploadQueue = filesToUpload.slice(0, remainingSlots);

        if (!uploadQueue.length) return;

        try {
            for (const selectedFile of uploadQueue) {
                if (!validateFileBeforeUpload(selectedFile)) continue;

                const isVideo = selectedFile.type?.startsWith('video/') || isVideoFile(selectedFile.name);
                const isImg = selectedFile.type?.startsWith('image/') || isImageFile(selectedFile.name);
                const localPreviewUrl = (isVideo || isImg)
                    ? URL.createObjectURL(selectedFile)
                    : null;

                const formData = new FormData();
                formData.append('media', selectedFile);
                formData.append('destination', destination);

                const result = await dispatch(uploadFiles(formData)).unwrap();
                const extracted = extractUploadedFileEntries(result);
                const filesWithPreview = extracted.map((entry) => {
                    if (typeof entry === 'object' && entry !== null) {
                        return {
                            ...entry,
                            originalName: entry.originalName || entry.original_name || selectedFile.name,
                            previewUrl: localPreviewUrl || entry.previewUrl || buildPreviewUrl(resolveFilePath(entry)),
                        };
                    }
                    return {
                        fileName: entry,
                        originalName: selectedFile.name,
                        previewUrl: localPreviewUrl || buildPreviewUrl(entry),
                    };
                });

                applyNormalizedFiles(filesWithPreview);

                if (tripId && result?.data) {
                    const payload = {
                        trip_id: tripId,
                        files: result.data
                    };
                    await dispatch(uploadedFuelFiles(payload)).unwrap();
                }
            }

            successNotify(uploadQueue.length > 1 ? 'Files Uploaded successfully' : 'File Uploaded successfully');
        } catch (err) {
            errorNotify(err?.message || err || 'File upload failed');
        } finally {
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        }
    };

    const handleDrop = async (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length) {
            await uploadSelectedFiles(e.dataTransfer.files);
        }
    };

    const handleChange = async (e) => {
        if (disabled) return;
        e.preventDefault();
        if (e.target.files && e.target.files.length) {
            await uploadSelectedFiles(e.target.files);
        } else {
            errorNotify('No file selected for upload');
        }
    };

    const getFileIcon = (fileName) => {
        if (!fileName || typeof fileName !== 'string') return <FaFile style={{ color: '#666' }} />;
        const lowerName = fileName.toLowerCase();

        if (lowerName.includes('fmcr') || lowerName.includes('fleet-monitoring')) {
            return <FiBarChart2 style={{ fontSize: '26px', color: '#4318FF' }} />;
        }
        if (lowerName.includes('customer')) {
            return <FiUsers style={{ fontSize: '26px', color: '#4318FF' }} />;
        }
        if (lowerName.includes('driver')) {
            return <RiSteering2Line style={{ fontSize: '26px', color: '#4318FF' }} />;
        }
        if (lowerName.includes('trip')) {
            return <span style={{ display: 'inline-flex', filter: 'invert(16%) sepia(90%) saturate(6000%) hue-rotate(245deg)' }}>{tripSidebarIcon}</span>;
        }
        if (lowerName.includes('fuel')) {
            return <span style={{ display: 'inline-flex', filter: 'invert(16%) sepia(90%) saturate(6000%) hue-rotate(245deg)' }}>{fuelSidebarIcon}</span>;
        }
        if (lowerName.includes('inventory') || lowerName.includes('part') || lowerName.includes('procurement') || lowerName.includes('warehouse') || lowerName.includes('vendor')) {
            return <span style={{ display: 'inline-flex', filter: 'invert(16%) sepia(90%) saturate(6000%) hue-rotate(245deg)' }}>{inventoryIcon}</span>;
        }
        if (lowerName.includes('asset')) {
            return <span style={{ display: 'inline-flex', filter: 'invert(16%) sepia(90%) saturate(6000%) hue-rotate(245deg)' }}>{inventoryIcon}</span>;
        }
        if (lowerName.includes('maintenance') || lowerName.includes('issue') || lowerName.includes('service') || lowerName.includes('work-order')) {
            return <BsWrenchAdjustable style={{ fontSize: '26px', color: '#4318FF' }} />;
        }
        if (lowerName.includes('contract')) {
            return <span style={{ display: 'inline-flex', filter: 'invert(16%) sepia(90%) saturate(6000%) hue-rotate(245deg)' }}>{contractSidebarIcon}</span>;
        }
        if (lowerName.includes('3pl') || lowerName.includes('transporter')) {
            return <MdLocalShipping style={{ fontSize: '26px', color: '#4318FF' }} />;
        }
        if (lowerName.includes('finance') || lowerName.includes('invoice') || lowerName.includes('payroll')) {
            return <RiMoneyDollarCircleLine style={{ fontSize: '26px', color: '#4318FF' }} />;
        }
        if (lowerName.includes('role')) {
            return <FiShield style={{ fontSize: '26px', color: '#4318FF' }} />;
        }
        if (lowerName.includes('access')) {
            return <span style={{ display: 'inline-flex', filter: 'invert(16%) sepia(90%) saturate(6000%) hue-rotate(245deg)' }}>{accessManagementIcon}</span>;
        }
        if (lowerName.includes('module')) {
            return <FiLayers style={{ fontSize: '26px', color: '#4318FF' }} />;
        }

        const extension = fileName.split('.').pop().toLowerCase();

        switch (extension) {
            case 'pdf':
                return <FaFilePdf style={{ color: '#d32f2f' }} />;
            case 'doc':
            case 'docx':
                return <FaFileWord style={{ color: '#1976d2' }} />;
            case 'xls':
            case 'xlsx':
                return <FaFileExcel style={{ color: '#388e3c' }} />;
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
            case 'webp':
            case 'svg':
                return <FaFileImage style={{ color: '#ff9800' }} />;
            case 'mp4':
            case 'mov':
            case 'avi':
            case 'mkv':
            case 'webm':
            case 'm4v':
                return <FaFileVideo style={{ color: '#e0245e' }} />;
            case 'zip':
            case 'rar':
            case '7z':
                return <FaFileArchive style={{ color: '#6d4c41' }} />;
            default:
                return <FaFile style={{ color: '#666' }} />;
        }
    };

    const isImageFile = (fileName) => {
        if (!fileName || typeof fileName !== 'string') return false;
        const extension = fileName.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension);
    };

    const isVideoFile = (fileName) => {
        if (!fileName || typeof fileName !== 'string') return false;
        const extension = fileName.split('.').pop().toLowerCase();
        return ['mp4', 'mov', 'avi', 'mkv', 'webm', 'm4v', '3gp', 'wmv', 'flv'].includes(extension);
    };

    const resolveDeleteKey = (fileEntry) => {
        const galleryFileId = resolveGalleryFileId(fileEntry);
        return galleryFileId !== null ? String(galleryFileId) : '';
    };

    const handleFiles = useCallback((filesArr) => {
        applyNormalizedFiles(filesArr);
        dispatch(clearUploadedFiles());
    }, [applyNormalizedFiles, dispatch]);

    useEffect(() => {
        if (!consumeUploadedFiles) return;
        const normalizedUploadedFiles = normalizeToArray(uploadedFiles);
        if (normalizedUploadedFiles.length > 0) {
            handleFiles(normalizedUploadedFiles);
        }
    }, [uploadedFiles, handleFiles, consumeUploadedFiles]);

    const onButtonClick = () => {
        if (disabled) return;
        inputRef.current?.click();
    };

    const handleAreaKeyDown = (e) => {
        if (disabled || (maxFiles && files.length >= maxFiles)) return;
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onButtonClick();
        }
    };

    const removeFile = (file, indexToRemove) => {
        const deleteKey = resolveDeleteKey(file);
        setFileToDelete({ file, index: indexToRemove, deleteKey });
        setShowDeleteModal(true);
    };

    const handleDeleteConfirm = async () => {
        if (!fileToDelete) return;

        const { file, index, deleteKey } = fileToDelete;
        setDeleteLoading(true);

        try {
            if (deleteKey) {
                await apiService.deleteGalleryFile(deleteKey);
            }

            if (isObjectUrl(file?.previewUrl)) {
                URL.revokeObjectURL(file.previewUrl);
            }

            successNotify('File deleted successfully');

            const updatedFiles = files.filter((_, idx) => idx !== index);
            if (typeof setFiles === 'function') {
                setFiles(updatedFiles);
            }
            setShowDeleteModal(false);
            setFileToDelete(null);
        } catch (error) {
            errorNotify(error?.message || 'Failed to delete file');
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className={`file_upload_container ${className} ${disabled ? 'file_upload_disabled' : ''}`}>
            <div
                className={`file_upload_area ${dragActive ? 'drag_active' : ''} ${(maxFiles && files.length >= maxFiles) ? 'max_files_reached' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={(maxFiles && files.length >= maxFiles) ? (e) => e.preventDefault() : handleDrop}
                onClick={(maxFiles && files.length >= maxFiles) ? null : onButtonClick}
                onKeyDown={handleAreaKeyDown}
                tabIndex={disabled ? -1 : (typeof tabIndex === 'number' ? tabIndex : 0)}
                style={(disabled || (maxFiles && files.length >= maxFiles)) ? { pointerEvents: (maxFiles && files.length >= maxFiles) ? 'none' : 'none', opacity: 0.6, cursor: 'not-allowed' } : null}
            >
                <input
                    ref={inputRef}
                    type="file"
                    onChange={handleChange}
                    multiple
                    style={{ display: 'none' }}
                    accept={accept}
                    disabled={disabled}
                    tabIndex={-1}
                />
                <div className='upload_content'>
                    <span className='upload_icon'>{uploadIcon}</span>
                    <div className='upload_text'>
                        <span>Drag file or Browse to select.</span>
                        <span>
                            Max file size: {maxFileSize}MB per file
                            {minFiles && ` | Min required files: ${minFiles}`}
                        </span>
                    </div>
                </div>
            </div>

            {uploadLoading && (
                <div className='upload_loading'>
                    <span>Processing files...</span>
                </div>
            )}

            {files.length > 0 && (
                <div className='uploaded_files_list'>
                    <div className='files_grid'>
                        {files.map((file, index) => {
                            const normalizedFile = normalizeDisplayFile(file);
                            const fileLink = normalizedFile.previewUrl || buildPreviewUrl(normalizedFile.fileName);
                            const fileLabel = normalizedFile.originalName || normalizedFile.fileName;
                            const originalFileName = getOriginalFileName(fileLabel) || fileLabel;
                            const shortFileName = getShortFileName(fileLabel);
                            const isRemovable = !disabled && file?.readOnly !== true && file?.removable !== false;
                            const shouldRenderImage =
                                isImageFile(normalizedFile.originalName || normalizedFile.fileName) &&
                                Boolean(fileLink);
                            const shouldRenderVideo =
                                isVideoFile(normalizedFile.originalName || normalizedFile.fileName) &&
                                Boolean(fileLink);

                            return (
                                <div className='files_grid_item' key={`${normalizedFile.fileName || 'file'}-${index}`}>
                                    <div className='uploaded_file_item'>
                                        <a
                                            href={fileLink || '#'}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="uploaded_file_link"
                                        >
                                            <div className='file_preview'>
                                                {shouldRenderImage ? (
                                                    <img
                                                        src={fileLink}
                                                        alt={fileLabel}
                                                        className='file_image_preview'
                                                    />
                                                ) : shouldRenderVideo ? (
                                                    <VideoThumbnail
                                                        src={fileLink}
                                                        alt={fileLabel}
                                                    />
                                                ) : (
                                                    <div className='file_icon_preview'>
                                                        {getFileIcon(fileLabel)}
                                                    </div>
                                                )}
                                            </div>
                                            <div className='file_info'>
                                                <span className='file_name' title={originalFileName} style={{ color: '#333', display: 'block' }}>
                                                    {shortFileName || originalFileName}
                                                </span>
                                            </div>
                                        </a>
                                        {
                                            isRemovable &&
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeFile(file, index);
                                                }}
                                                className='remove_file_btn'
                                                style={{ zIndex: "2" }}
                                            >
                                                <MdOutlineClose />
                                            </button>
                                        }
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {/* <div className='files_grid'>

                    </div> */}
                </div>
            )}

            <DeleteModal
                open={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false);
                    setFileToDelete(null);
                }}
                onLogout={handleDeleteConfirm}
                loading={deleteLoading}
                topHeading="Delete File"
                description={
                    <>
                        Are you sure you want to delete
                        <span className="confirm_delete_file_name" title={deleteFileName}>{shortDeleteFileName}?</span>
                    </>
                }
                subDescription="This action cannot be undone."
            />
        </div>
    );
};

export default FileUpload;
